import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchUserProfileTenant } from 'common/services/api/UserProfileGlobalRoles/UserProfileGlobalRoles.thunk';
import { GlobalRole } from 'common/types/Entities';
import { createMemoizedKeySelector } from 'common/utils/selectorUtils';

interface UserProfileGlobalRolesSliceState {
    processing: string | false;
    userGlobalRolesByTenantId: Record<string, GlobalRole[]>;
}

type StateWithUserProfileGlobalRoles = { userProfileGlobalRoles: UserProfileGlobalRolesSliceState };

const initialState: UserProfileGlobalRolesSliceState = {
    processing: false,
    userGlobalRolesByTenantId: {},
};

const userProfileGlobalRolesSlice = createSlice({
    name: 'userProfileGlobalRoles',
    initialState,

    reducers: (create) => ({
        clearUserProfileGlobalRoles: create.reducer(state => {
            state.userGlobalRolesByTenantId = {};
        })
    }),

    extraReducers: builder => {
        builder.addCase(fetchUserProfileTenant.pending, (state, args) => {
            state.processing = args.meta.arg.tenantId;
            state.userGlobalRolesByTenantId[args.meta.arg.tenantId] = [];
        });
        builder.addCase(fetchUserProfileTenant.fulfilled, (state, action: PayloadAction<{ tenantId: string, globalRoles: GlobalRole[] }>) => {
            state.processing = false;
            if (action.payload.globalRoles?.length) {
                const tenantId = action.payload.tenantId;
                state.userGlobalRolesByTenantId = {
                    ...state.userGlobalRolesByTenantId,
                    [tenantId]: action.payload.globalRoles
                };
            }
        });
        builder.addCase(fetchUserProfileTenant.rejected, (state, args) => {
            state.processing = false;
            state.userGlobalRolesByTenantId[args.meta.arg.tenantId] = [];
        });
    }
});

// eslint-disable-next-line no-restricted-syntax
export default userProfileGlobalRolesSlice.reducer;
export const { clearUserProfileGlobalRoles } = userProfileGlobalRolesSlice.actions;

// Base selector for byTenantId state
const selectUserGlobalRolesByTenantIdState = (state: StateWithUserProfileGlobalRoles) => state.userProfileGlobalRoles.userGlobalRolesByTenantId;

// Memoized parameterized selector
export const selectUserProfileGlobalRolesByTenantId = createMemoizedKeySelector(selectUserGlobalRolesByTenantIdState);
export const selectProcessing = (state: StateWithUserProfileGlobalRoles) => state.userProfileGlobalRoles.processing;
